import React, {
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useContext,
  memo,
} from 'react';
import {
  Form,
  Input,
  InputNumber,
  InputProps,
  InputNumberProps,
  Select,
  SelectProps,
  Tag,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { EditableCellWrap } from './styles';
import { EditableTableContext } from '../EditableRow';

export interface IEditableCell {
  dataIndex: string;
  children?: ReactNode;
  handleSave?: Function;
  itemInputType?: 'input' | 'input-number' | 'select';
  editable?: boolean;
  record?: any;
  isRequired?: boolean;
  selectProps?: SelectProps;
  inputProps?: InputProps;
  inputNumberProps?: InputNumberProps;
}

const EditableCell = ({
  children,
  dataIndex,
  record,
  handleSave,
  editable,
  selectProps,
  inputProps,
  inputNumberProps,
  itemInputType = 'input',
  isRequired = false,
  ...restProps
}: IEditableCell) => {
  const [editing, setEditing] = useState(false);
  const { form } = useContext(EditableTableContext);

  const value = useMemo(() => record?.[dataIndex], [dataIndex, record]);

  const { t } = useTranslation('table');

  const toggleEdit = useCallback(() => {
    setEditing(!editing);
    form?.setFieldsValue({
      [dataIndex]: value ?? '',
    });
  }, [dataIndex, form, value, editing]);

  const save = useCallback(async () => {
    const values = await form?.validateFields();
    toggleEdit();
    handleSave && handleSave({ ...record, ...values });
  }, [form, handleSave, record, toggleEdit]);

  const formItemEl = useMemo(() => {
    const itemProps = {
      autoFocus: true,
      onPressEnter: save,
      onBlur: save,
      style: {
        width: '100%',
      },
    };

    switch (itemInputType) {
      case 'input':
        return <Input {...itemProps} {...inputProps} />;
      case 'input-number':
        return <InputNumber step={0.01} {...itemProps} {...inputNumberProps} />;
      case 'select':
        return (
          <Select
            defaultOpen
            onSelect={save}
            filterOption={(inputValue: string, option: any) => {
              return option?.label
                .toLowerCase()
                .includes(inputValue?.toLowerCase());
            }}
            {...itemProps}
            {...selectProps}
          />
        );
      default:
        return <></>;
    }
  }, [inputNumberProps, inputProps, itemInputType, save, selectProps]);

  const cellValue = useMemo(() => {
    if (itemInputType !== 'select') {
      if (value) {
        const addonBefore =
          inputProps?.addonBefore || inputNumberProps?.addonBefore;
        if (addonBefore) {
          return `${addonBefore} ${value}`;
        }
        const addonAfter =
          inputProps?.addonAfter || inputNumberProps?.addonAfter;
        if (addonAfter) {
          return `${value} ${addonAfter}`;
        }
      }
      return value;
    }

    const getLabelByValue = (valueToLabel: number) =>
      selectProps?.options?.find(opt => opt.value === valueToLabel)?.label;

    const justANumber = typeof value === 'number';
    return selectProps?.mode === 'multiple' && !justANumber
      ? value?.map((val: number) => <Tag key={val}>{getLabelByValue(val)}</Tag>)
      : getLabelByValue(value);
  }, [
    inputNumberProps?.addonAfter,
    inputNumberProps?.addonBefore,
    inputProps?.addonAfter,
    inputProps?.addonBefore,
    itemInputType,
    selectProps?.mode,
    selectProps?.options,
    value,
  ]);

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: isRequired,
            message: t('editable.is-required'),
          },
        ]}
      >
        {formItemEl}
      </Form.Item>
    ) : (
      <EditableCellWrap onClick={toggleEdit}>{cellValue}</EditableCellWrap>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

export default memo(EditableCell);
